import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import { formatToCompanyTimezone } from "../../../../lib/functions";

const format = commaNumber.bindWith(",", ".");

const JournalRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.journalRecord
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // Parent-level columns
  const parentColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "jrnCode" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Journal By", dataIndex: "journalBy" },
    {
    title: "Account",
    render: (record) =>
      record.details?.length > 0
        ? record.details[0].acc_name
        : "-",
    },
    {
      title: "Debit Amount",
      dataIndex: "debitTotal",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
    {
      title: "Credit Amount",
      dataIndex: "creditTotal",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
  ];

  // Child detail columns
  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Account", dataIndex: "acc_name" },
    {
      title: "Debit Amount",
      dataIndex: "debit_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
    {
      title: "Credit Amount",
      dataIndex: "credit_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
  ];

  // Flat columns for without details
  const withoutDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "jrnCode" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Journal By", dataIndex: "journalBy" },
    {
      title: "Debit Total",
      dataIndex: "debitTotal",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
    {
      title: "Credit Total",
      dataIndex: "creditTotal",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
  ];

  const byAccountDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "jrnCode" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Receipt By", dataIndex: "journalBy" },
    { title: "Account", dataIndex: "account" },
    {
      title: "Debit Total",
      dataIndex: "debitTotal",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
    {
      title: "Credit Total",
      dataIndex: "creditTotal",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
  ];

  const columns =
    filters.filterType === "By Account"
      ? byAccountDetailsColumns
      : filters.recordType === "with details"
      ? parentColumns
      : withoutDetailsColumns;

  // Grand totals
  const grandDebit = list.reduce((sum, row) => {
    if (filters.recordType === "with details") {
      return (
        sum + row.details?.reduce((s, d) => s + Number(d.debit_amount || 0), 0)
      );
    }
    return sum + Number(row.debitTotal || 0);
  }, 0);

  const grandCredit = list.reduce((sum, row) => {
    if (filters.recordType === "with details") {
      return (
        sum + row.details?.reduce((s, d) => s + Number(d.credit_amount || 0), 0)
      );
    }
    return sum + Number(row.creditTotal || 0);
  }, 0);

  if (printMode && filters.filterType === "By Account") {
  return (
    <table className="journal-print-table">
      <colgroup>
        <col style={{ width: "6%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "20%" }} />
        <col style={{ width: "25%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
      </colgroup>

      <tbody>
        <tr className="journal-print-heading">
          <td>No.</td>
          <td>VCH No</td>
          <td>Date</td>
          <td>Receipt By</td>
          <td>Account</td>
          <td style={{ textAlign: "right" }}>Debit</td>
          <td style={{ textAlign: "right" }}>Credit</td>
        </tr>

        {list.map((row, index) => (
          <tr key={row.id || index}>
            <td>{index + 1}</td>
            <td>{row.jrnCode}</td>
            <td>{row.date ? formatToCompanyTimezone(row.date).split(" ")[0] : "-"}</td>
            <td>{row.journalBy}</td>
            <td>{row.account}</td>
            <td style={{ textAlign: "right" }}>{format(Number(row.debitTotal || 0).toFixed(2))}</td>
            <td style={{ textAlign: "right" }}>{format(Number(row.creditTotal || 0).toFixed(2))}</td>
          </tr>
        ))}

        <tr className="journal-print-total">
          <td colSpan={5} style={{ textAlign: "right" }}>Grand Total</td>
          <td style={{ textAlign: "right" }}>{format(grandDebit.toFixed(2))}</td>
          <td style={{ textAlign: "right" }}>{format(grandCredit.toFixed(2))}</td>
        </tr>
      </tbody>
    </table>
  );
}


if (printMode && filters.recordType === "with details") {
  return (
    <table className="journal-print-table">
      <tbody>
        <tr className="journal-print-heading">
          <td>No.</td>
          <td>VCH No</td>
          <td>Date</td>
          <td>Journal By</td>
          <td>Account</td>
          <td>Debit</td>
          <td>Credit</td>
        </tr>

        {list.map((row, index) => {
          const subtotalDebit =
            row.details?.reduce(
              (sum, d) => sum + Number(d.debit_amount || 0),
              0
            ) || 0;

          const subtotalCredit =
            row.details?.reduce(
              (sum, d) => sum + Number(d.credit_amount || 0),
              0
            ) || 0;

          return (
            <React.Fragment key={row.id || index}>
              <tr>
                <td>{index + 1}</td>
                <td>{row.jrnCode}</td>
                <td>
                  {row.date
                    ? formatToCompanyTimezone(row.date).split(" ")[0]
                    : "-"}
                </td>
                <td>{row.journalBy}</td>
                <td>
                  {row.details?.[0]?.acc_name || "-"}
                </td>
                <td style={{ textAlign: "right" }}>
                  {format(Number(row.debitTotal || 0).toFixed(2))}
                </td>
                <td style={{ textAlign: "right" }}>
                  {format(Number(row.creditTotal || 0).toFixed(2))}
                </td>
              </tr>

              {row.details?.map((detail, i) => (
                <tr
                  key={i}
                  className="journal-detail-row"
                >
                  <td />
                  <td colSpan={3}>
                    Detail #{i + 1}
                  </td>
                  <td>{detail.acc_name}</td>
                  <td style={{ textAlign: "right" }}>
                    {format(Number(detail.debit_amount || 0).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(Number(detail.credit_amount || 0).toFixed(2))}
                  </td>
                </tr>
              ))}

              <tr className="journal-subtotal-row">
                <td colSpan={5} style={{ textAlign: "right" }}>
                  Sub Total
                </td>

                <td style={{ textAlign: "right" }}>
                  {format(subtotalDebit.toFixed(2))}
                </td>

                <td style={{ textAlign: "right" }}>
                  {format(subtotalCredit.toFixed(2))}
                </td>
              </tr>
            </React.Fragment>
          );
        })}

        <tr className="journal-print-total">
          <td colSpan={5} style={{ textAlign: "right" }}>
            Grand Total
          </td>

          <td style={{ textAlign: "right" }}>
            {format(grandDebit.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(grandCredit.toFixed(2))}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

  if (printMode) {
  return (
    <table className="journal-print-table">
      <colgroup>
        <col style={{ width: "6%" }} />
        <col style={{ width: "16%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "34%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "15%" }} />
      </colgroup>

      <tbody>
        <tr className="journal-print-heading">
          <td>No.</td>
          <td>VCH No</td>
          <td>Date</td>
          <td>Journal By</td>
          <td style={{ textAlign: "right" }}>Debit Total</td>
          <td style={{ textAlign: "right" }}>Credit Total</td>
        </tr>

        {list.map((row, index) => (
          <tr key={row.id || index}>
            <td>{index + 1}</td>

            <td>{row.jrnCode}</td>

            <td>
              {row.date
                ? formatToCompanyTimezone(row.date).split(" ")[0]
                : "-"}
            </td>

            <td>{row.journalBy}</td>

            <td style={{ textAlign: "right" }}>
              {format(Number(row.debitTotal || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(Number(row.creditTotal || 0).toFixed(2))}
            </td>
          </tr>
        ))}

        <tr className="journal-print-total">
          <td colSpan={4} style={{ textAlign: "right" }}>
            Grand Total
          </td>

          <td style={{ textAlign: "right" }}>
            {format(grandDebit.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(grandCredit.toFixed(2))}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      size={printMode ? "small" : "middle"}
      expandable={
        filters.recordType === "with details"
          ? {
              expandedRowRender: (record) => {
                const subtotalDebit = record.details?.reduce(
                  (sum, d) => sum + Number(d.debit_amount || 0),
                  0
                );
                const subtotalCredit = record.details?.reduce(
                  (sum, d) => sum + Number(d.credit_amount || 0),
                  0
                );

                return (
                  <Table
                    rowKey="detailId"
                    columns={detailColumns}
                    dataSource={record.details || []}
                    pagination={false}
                    size="small"
                    bordered
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2} align="right">
                          <strong>Sub Total</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <strong>{subtotalDebit.toFixed(2)}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <strong>{subtotalCredit.toFixed(2)}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                );
              },
              rowExpandable: (record) => record.details?.length > 0,
            }
          : undefined
      }
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell
            index={0}
            colSpan={
              filters.recordType === "with details"
                ? columns.length - 1
                : columns.length - 2
            }
            align="right"
          >
            <strong>Grand Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={columns.length - 2} align="right">
            <strong>{grandDebit.toFixed(2)}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={columns.length - 1} align="right">
            <strong>{grandCredit.toFixed(2)}</strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default JournalRecordTable;
